import React, { useEffect, useMemo, useState } from 'react';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import {SortableElement} from 'react-sortable-hoc';
import {Alert, Autocomplete, Avatar, Badge, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, ListItem, ListItemAvatar, ListItemText, Stack, Step, StepContent, StepLabel, Stepper, Switch, TextField, Tooltip} from "@mui/material";
import {Autocomplete as MapsAutocomplete} from "@react-google-maps/api";
import Typography from "@mui/material/Typography";
import StateBadge from 'app/widgets/Shared/StateBadge';
import moment from 'moment';
import _ from 'lodash';
import orderServices from 'app/services/order-services';
import { encodeParams } from 'app/utils/appHelpers';
import tripServices from 'app/services/trip-services';
import { LoadingButton } from '@mui/lab';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import TrashIconButton from 'app/shared/widgets/TrashIconButton';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import customerServices from 'app/services/customer-services';
import deliveryServices from 'app/services/delivery-services';
import LocationDialog from 'app/pages/customers/LocationDialog';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import placeServices from 'app/services/place-services';
import { useContext } from "react";
import { AuthContext } from "@jumbo/components/JumboAuthProvider/JumboAuthContext"
import CarrierPickupSearch from '../planification/CarrierPickupSearch';
import { getDropoffCarrierByKey, getDropoffCarrierOptions } from 'app/utils/dropoffCarriers';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { useSnackbar } from 'notistack';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import PrimaryAddressChip from 'app/shared/widgets/PrimaryAddressChip';
import OrdersPicker from './OrdersPicker';
import { useCompanySettings } from 'app/providers/CompanySettingsProvider/CompanySettingsProvider';
import { stopNumbersByDeliveryId } from 'app/utils/tripStops';

const getLocatableDropoffSearchAddress = (locatable) => {
    const location = locatable?.location;
    const manualAddress = _.join(_.compact([
        location?.short_address,
        location?.short_zone,
    ]), ', ');

    return location?.search_query || manualAddress || '';
};

const getPlaceDropoffSearchAddress = (place, fallbackAddress = '') => {
    const locatableAddress = getLocatableDropoffSearchAddress(place);
    return locatableAddress || fallbackAddress || '';
};

const normalizeDropoffPoint = (point, fallbackCarrier = null) => {
    if (!point) return null;

    return {
        id: point.id || `${point.lat}-${point.lng}-${point.carrier || fallbackCarrier || 'dropoff'}`,
        name: point.name || 'Punto de dropoff',
        lat: point.lat,
        lng: point.lng,
        carrier: point.carrier || fallbackCarrier,
        address_hint: point.address_hint || '',
        distance_km_hint: point.distance_km_hint,
    };
};

const ActionButtons = ({opts=[]}) => {
    return (
        <Stack direction="row" spacing={1}>
            { _.map(opts, (opt, idx) => {
                // `tooltip` sale del resto: si entrara al spread, LoadingButton lo bajaria al
                // DOM y React avisaria por un atributo que no existe.
                const {tooltip, ...buttonProps} = opt;

                const button = (
                    <LoadingButton {...buttonProps} size={opt.size || 'small'} color={opt.color || 'primary'} variant={opt.variant || 'contained'} >
                        {opt.label}
                    </LoadingButton>
                );

                if(!tooltip) return <React.Fragment key={`abs_${idx}`}>{button}</React.Fragment>;

                // El span es necesario: un boton deshabilitado no emite eventos del mouse y el
                // tooltip no se mostraria, justo cuando mas hace falta saber por que no se puede.
                return (
                    <Tooltip arrow key={`abs_${idx}`} title={tooltip}>
                        <span>{button}</span>
                    </Tooltip>
                );
            })}
        </Stack>
    )
}

/**
 * Icono del courier del dropoff, para las entregas que se retiran en un punto en vez de
 * entregarse a domicilio. En la lista del instructivo esas entregas se veian igual que
 * cualquier otra, asi que quien planificaba tenia que abrir cada una para saber cual era.
 *
 * Cae al nombre en texto cuando el courier no tiene icono en el catalogo, que es el caso de
 * una compania dropoff propia cargada sin imagen. Es lo mismo que hace la tarjeta del viaje.
 */
const DropoffCarrierMark = ({carrierKey, pointName, carriers}) => {
    const carrier = getDropoffCarrierByKey(carrierKey, carriers);
    const label = carrier?.label || carrierKey;
    const title = pointName ? `Retiro en ${label}: ${pointName}` : `Retiro en ${label}`;

    if(!carrier?.iconPath) {
        return (
            <Tooltip arrow title={title}>
                <Chip size="small" variant="outlined" label={label} sx={{height: 20, fontSize: 11}}/>
            </Tooltip>
        );
    }

    return (
        <Tooltip arrow title={title}>
            <img
                src={carrier.iconPath}
                alt={label}
                style={{height: 16, borderRadius: 4, border: '1px solid #eee', flexShrink: 0}}
            />
        </Tooltip>
    );
};

const DeliveryCell = ({tripId, tripState, delivery, deliveries=[], canSort, canManageTrip=true, stop, formNew=false, isLastDelivery=false, onSelect=()=> {}, onAdd=()=> {}, onRemove=()=> {}, onAddressChanged=()=> {}, mapsLoaded=false, dropoffMapPoint=null, onDropoffMapContextChange=()=> {}, onDropoffSearch=()=> {}, onDropoffSearchClear=()=> {}, dropoffSearchLoading=false, selectMode=false, selectedForRemoval=false, onToggleSelectForRemoval=()=> {}}) => {
    const [addMethod, setAddMethod] = useState(null); // address and name / customer / order / bulk_dropoff
    const { authUser } = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const { companySettings } = useCompanySettings();
    const dropoffCarrierOptions = useMemo(
        () => getDropoffCarrierOptions(companySettings?.available_dropoff_carriers),
        [companySettings?.available_dropoff_carriers]
    );

    const [newOrder, setNewOrder] = useState(null);
    const [newCustomer, setNewCustomer] = useState(null);
    const [comment, setComment] = useState("");

    const [newPlace, setNewPlace] = useState(null);
    const [placeName, setPlaceName] = useState("");
    const [placeAddress, setPlaceAddress] = useState("");
    const [searchResult, setSearchResult] = useState(null);

    const [addingStop, setAddingStop] = useState(false);
    // Modal de elección de dirección (cliente con >1 dirección) al agregar parada.
    const [addressChoice, setAddressChoice] = useState({ open: false, addresses: [], label: '', onSelect: null });
    // Editor de direcciones y geocercas del cliente de la parada (icono de mapa de la fila).
    const [openLocDialog, setOpenLocDialog] = useState(false);
    const [removingStopId, setRemovingStopId] = useState(null);
    // Respuesta order_already_assigned del backend pendiente de confirmar, o null.
    const [confirmReassign, setConfirmReassign] = useState(null);
    // Pedidos marcados en la lista, con el objeto completo para poder nombrarlos al confirmar.
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [confirmAddOrders, setConfirmAddOrders] = useState(false);
    // Los pedidos que el backend contesto que ya estan en otro viaje, o null. Traerlos los saca
    // de ese viaje, asi que no se hace sin confirmar.
    const [confirmReassignOrders, setConfirmReassignOrders] = useState(null);
    const [confirmRemoveDelivery, setConfirmRemoveDelivery] = useState(false);

    const [ordersOptsLoading, setOrdersOptsLoading] = useState(false);
    const [customersOptsLoading, setCustomersOptsLoading] = useState(false);
    const [placesOptsLoading, setPlacesOptsLoading] = useState(false);

    const [ordersOpts, setOrdersOpts] = useState([]);
    const [customersOpts, setCustomersOpts] = useState([]);
    const [placesOpts, setPlacesOpts] = useState([]);

    const [dropoffEnabled, setDropoffEnabled] = useState(false);
    const [dropoffCarrier, setDropoffCarrier] = useState(null);
    const [selectedDropoffPoint, setSelectedDropoffPoint] = useState(null);

    // Paso a paso de dropoff masivo sobre los pedidos que ya estan en el viaje.
    // Va aparte del dropoff de "agregar parada": ese asigna uno al crear la
    // entrega, este edita varios que ya existen.
    const [bulkStep, setBulkStep] = useState(0);
    const [bulkAction, setBulkAction] = useState('assign'); // assign | remove
    const [bulkOrderIds, setBulkOrderIds] = useState([]);
    const [bulkCarrier, setBulkCarrier] = useState(null);
    const [bulkPoint, setBulkPoint] = useState(null);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const [confirmBulkDropoff, setConfirmBulkDropoff] = useState(false);

    const getOrdersOptions = async (search) => {
        setOrdersOptsLoading(true)
        var params = encodeParams(_.join([
            "include=locatable,locatable.location",
            "with_delivery_state=true",
            "q[state_not_in][]=deleted",
            "q[state_not_in][]=cancelled",
            "q[state_not_in][]=finished",
            `q[code_or_locatable_of_Customer_type_name_or_locatable_of_Place_type_name_cont]=${search}`
        ], '&'))
        orderServices.getOrders(`?${params}`)
            .then(data => {
                setOrdersOpts(data.data)
            })
            .finally((err) => {
                setOrdersOptsLoading(false)
            });
    }

    const getCustomersOptions = async (search) => {
        setCustomersOptsLoading(true)
        var params = encodeParams(_.join([
            "include=location",
            `q[name_cont]=${search}`
        ], '&'))
        customerServices.getCustomers(`?${params}`)
            .then(data => {
                setCustomersOpts(data.data)
            })
            .finally((err) => {
                setCustomersOptsLoading(false)
            });
    }

    const buildDropoffPayload = () => {
        if (!dropoffEnabled) return {};

        return {
            dropoff: true,
            dropoff_carrier: dropoffCarrier?.value,
            dropoff_point: selectedDropoffPoint,
        };
    };

    const buildAddByOrderPayload = (reassign = false) => {
        const shouldPersistDropoff = dropoffEnabled || Boolean(newOrder?.dropoff);
        const payload = {
            order_id: newOrder.id,
            comment: comment,
        };

        if (shouldPersistDropoff) {
            payload.dropoff = dropoffEnabled;
        }

        if (dropoffEnabled) {
            payload.dropoff_carrier = dropoffCarrier?.value;
            payload.dropoff_point = selectedDropoffPoint;
        }

        if (reassign) {
            payload.reassign = true;
        }

        if (newOrder?.planned_address) {
            payload.planned_address = newOrder.planned_address;
        }

        return payload;
    };

    const submitAddByOrder = (reassign = false) => {
        setAddingStop(true)
        return tripServices.addByOrder(tripId, buildAddByOrderPayload(reassign))
            .then(data => {
                setAddingStop(false)
                resetFormState()
                onAdd(data)
            })
    };

    const confirmReassignAndAdd = (data) => {
        setConfirmReassign(data);
    };

    /**
     * El aviso del alta multiple: nombra cada pedido y el viaje del que va a salir.
     *
     * Se listan uno por uno y no en un total porque cada uno puede venir de un viaje distinto, y
     * la consecuencia -que ese viaje se elimine si se queda sin entregas- es de cada uno.
     */
    const reassignOrdersMessage = (items = []) => (
        <>
            {items.length === 1
                ? <>¿Estás seguro de que quieres agregar el pedido <strong>{items[0]?.code}</strong> al viaje <strong>{tripId}</strong>?</>
                : <>¿Estás seguro de que quieres agregar estos <strong>{items.length} pedidos</strong> al viaje <strong>{tripId}</strong>?</>}
            <br />
            {items.map((item) => (
                <React.Fragment key={item.order_id}>
                    <br />
                    {item.trip_code
                        ? <>El pedido <strong>{item.code}</strong> está en el viaje <strong>N°{item.trip_code}</strong> y se eliminará de ese viaje.</>
                        : <>El pedido <strong>{item.code}</strong> está en otro viaje activo y se eliminará de ese viaje.</>}
                    {item.trip_will_be_deleted && (
                        <>
                            {' '}
                            {item.trip_code
                                ? <>El viaje <strong>N°{item.trip_code}</strong> se eliminará porque no le quedan más pedidos.</>
                                : <>Ese viaje se eliminará porque no le quedan más pedidos.</>}
                        </>
                    )}
                </React.Fragment>
            ))}
        </>
    );

    const reassignMessage = (data) => {
        const otherTripCode = data?.other_trip_code;
        const orderInfo = otherTripCode ? <>el viaje <strong>N°{otherTripCode}</strong></> : 'otro viaje activo';

        return (
            <>
                Este pedido ya está asignado a {orderInfo}. Si continúas, se eliminará de ese viaje y se traerá a este.
                { data?.other_trip_will_be_deleted && (
                    <>
                        <br />
                        {otherTripCode
                            ? <>El viaje <strong>N°{otherTripCode}</strong> se eliminará porque no tiene más pedidos.</>
                            : <>El otro viaje se eliminará porque no tiene más pedidos.</>}
                    </>
                )}
            </>
        );
    };

    // Si el cliente del pedido/cliente tiene más de una dirección y aún no se
    // eligió una, abre el modal para elegirla antes de agregar la parada.
    const requireAddressThen = (addresses, label, onChosen) => {
        setAddressChoice({
            open: true,
            addresses,
            label,
            onSelect: (addr) => {
                setAddressChoice({ open: false, addresses: [], label: '', onSelect: null })
                onChosen({
                    customer_address_id: addr.id,
                    location_id: addr.location_id,
                    lat: addr.lat,
                    lng: addr.lng,
                })
            },
        })
    }

    const doAddByOrder = () => {
        submitAddByOrder(false)
            .catch((err) => {
                setAddingStop(false)
                const data = err?.response?.data;
                if (data?.error_code === 'order_already_assigned') {
                    confirmReassignAndAdd(data);
                }
            });
    }

    const addByOrder = async () => {
        if(!newOrder) { return }
        const addrs = newOrder?.locatable?.addresses || []
        if (addrs.length > 1 && !newOrder.planned_address) {
            requireAddressThen(addrs, `Pedido ${newOrder.code || ''}`, (pa) => {
                newOrder.planned_address = pa
                doAddByOrder()
            })
            return
        }
        doAddByOrder()
    }

    const selectedOrderIds = _.map(selectedOrders, 'id');

    const toggleOrder = (order) => {
        setSelectedOrders((previous) => (
            _.some(previous, { id: order.id })
                ? _.reject(previous, { id: order.id })
                : [...previous, order]
        ));
    };

    // El backend agrega los pedidos en una sola transaccion: o entran todos o no entra
    // ninguno, asi que aca no hay que ir mostrando avances parciales.
    const submitAddByOrders = (reassign = false) => {
        setConfirmAddOrders(false);
        setAddingStop(true);
        tripServices.addByOrders(tripId, {
            order_ids: selectedOrderIds,
            comment: comment,
            // Solo cuando ya se confirmo: sin esto el backend contesta el detalle de en que
            // viaje esta cada uno en vez de moverlos.
            ...(reassign ? { reassign: true } : {}),
        })
            .then(data => {
                setAddingStop(false)
                resetFormState()
                onAdd(data)
            })
            .catch((err) => {
                setAddingStop(false)
                const data = err?.response?.data;
                if (data?.error_code === 'orders_already_assigned') {
                    setConfirmReassignOrders(data.already_assigned || []);
                }
            });
    };

    const doAddByCustomer = () => {
        setAddingStop(true)
        tripServices.addByCustomer(tripId, {
            customer_id: newCustomer.id,
            comment: comment,
            planned_address: newCustomer.planned_address,
            ...buildDropoffPayload(),
        })
            .then(data => {
                setAddingStop(false)
                resetFormState()
                onAdd(data)
            })
            .catch((err) => {
                setAddingStop(false)
            });
    }

    const addByCustomer = async () => {
        if(!newCustomer) { return }
        const addrs = newCustomer?.addresses || []
        if (addrs.length > 1 && !newCustomer.planned_address) {
            requireAddressThen(addrs, newCustomer.name || 'Cliente', (pa) => {
                newCustomer.planned_address = pa
                doAddByCustomer()
            })
            return
        }
        doAddByCustomer()
    }

    const addByPlace = async () => {
        if(!(newPlace?.id || (placeName && placeAddress))) { return }
        setAddingStop(true)
        tripServices.addByPlace(tripId, {
            place_id: newPlace?.id,
            name: placeName,
            address: placeAddress,
            comment: comment,
            ...buildDropoffPayload(),
        })
            .then(data => {
                setAddingStop(false)
                resetFormState()
                onAdd(data)
            })
            .catch((err) => {
                setAddingStop(false)
            });
    }

    const removeDelivery = async () => {
        setConfirmRemoveDelivery(false)
        setRemovingStopId(delivery.id)
        tripServices.removeDelivery(tripId, { delivery_id: delivery.id })
            .then(data => {
                setRemovingStopId(null)
                // data.trip_deleted llega cuando era la ultima entrega: el padre tiene
                // que cerrar el instructivo en vez de recargarlo, porque ya no existe.
                onRemove(delivery.id, data)
            })
            .catch((err) => {
                setRemovingStopId(null)
                setAddingStop(false)
            });
    }

    function onLoad(autocomplete) {
        setSearchResult(autocomplete);
    }

    function onPlaceChanged() {
        if (searchResult != null) {
          const newAddr  = searchResult.getPlace().formatted_address;

          if(newAddr) {
            setPlaceAddress(newAddr)
          }
        }
    }

    const resetFormState = () => {
        setAddMethod(null);
        setNewOrder(null);
        setSelectedOrders([]);
        setConfirmAddOrders(false);
        setNewCustomer(null);
        setComment("");
        setNewPlace(null);
        setPlaceName("");
        setPlaceAddress("");
        setSearchResult(null);
        setDropoffEnabled(false);
        setDropoffCarrier(null);
        setSelectedDropoffPoint(null);
        resetBulkState();
    };

    const resetBulkState = () => {
        setBulkStep(0);
        setBulkAction('assign');
        setBulkOrderIds([]);
        setBulkCarrier(null);
        setBulkPoint(null);
        setBulkSubmitting(false);
        setConfirmBulkDropoff(false);
    };

    // Solo las entregas con pedido: el dropoff de una parada de cliente o de otro
    // lugar vive en la metadata de la entrega, no en el pedido, y este endpoint
    // trabaja sobre pedidos.
    const bulkOrderDeliveries = useMemo(() => (
        (deliveries || []).filter((d) => d?.order?.id && !_.includes(['cancelled'], d.state))
    ), [deliveries]);

    /**
     * Numero de parada de cada entrega, para poder mostrarlo al lado del pedido.
     *
     * Se recorre `deliveries` completa y no `bulkOrderDeliveries`: la numeracion la calcula
     * RouteBuilder sobre TODAS las entregas del viaje, asi que contar
     * sobre la lista ya filtrada daria numeros que no coinciden con los circulitos de la
     * lista de al lado, que es justamente para lo que sirven.
     *
     * Por eso usa el mismo calculo (utils/tripStops): el contador avanza cuando cambia el
     * destino, y las entregas seguidas al mismo lugar o punto de dropoff comparten numero.
     */
    const stopByDeliveryId = useMemo(() => stopNumbersByDeliveryId(deliveries), [deliveries]);

    /**
     * Los pedidos que se ofrecen para marcar.
     *
     * Quitar el dropoff de un pedido que no lo tiene no hace nada, asi que en esa accion no
     * se ofrecen: antes estaban todos y se podia mandar el lote entero a quitar algo que la
     * mayoria no tenia.
     *
     * Es una lista aparte de `bulkOrderDeliveries` a proposito: el paso a paso completo se
     * esconde cuando esa esta vacia, asi que filtrarla dejaria sin salida a quien entra a
     * "Quitar dropoff" en un viaje donde ninguno tiene —se quedaria sin los botones para
     * volver a "Asignar".
     */
    const bulkSelectableDeliveries = useMemo(() => (
        bulkAction === 'remove'
            ? bulkOrderDeliveries.filter((d) => Boolean(d.order?.dropoff))
            : bulkOrderDeliveries
    ), [bulkAction, bulkOrderDeliveries]);

    // Lo que quedaba marcado puede no estar mas en la lista al cambiar de accion. Sin esto,
    // "Quitar dropoff" arrancaba con pedidos sin dropoff ya seleccionados de antes y el
    // contador de "se le quita a N pedidos" mentia.
    useEffect(() => {
        const availableIds = bulkSelectableDeliveries.map((d) => d.order.id);
        setBulkOrderIds((previous) => {
            const next = previous.filter((id) => _.includes(availableIds, id));
            return next.length === previous.length ? previous : next;
        });
    }, [bulkSelectableDeliveries]);

    const toggleBulkOrder = (orderId) => {
        setBulkOrderIds((previous) => (
            _.includes(previous, orderId) ? _.without(previous, orderId) : [...previous, orderId]
        ));
    };

    const toggleAllBulkOrders = () => {
        setBulkOrderIds((previous) => (
            previous.length === bulkSelectableDeliveries.length
                ? []
                : bulkSelectableDeliveries.map((d) => d.order.id)
        ));
    };

    // La direccion por defecto del carrier, cuando esta cargada y geocodificada.
    // Es el mismo atajo que ofrece planificacion: evita buscar en el mapa un punto
    // que la empresa ya tiene configurado.
    const bulkCarrierDefaultAddress = useMemo(() => {
        if (!bulkCarrier?.value) return null;

        const catalogCarrier = getDropoffCarrierByKey(bulkCarrier.value, companySettings?.available_dropoff_carriers);
        const defaultAddress = catalogCarrier?.defaultAddress;
        if (!defaultAddress) return null;

        return _.isFinite(Number(defaultAddress.lat)) && _.isFinite(Number(defaultAddress.lng))
            ? defaultAddress
            : null;
    }, [bulkCarrier, companySettings?.available_dropoff_carriers]);

    const applyBulkDefaultAddress = () => {
        if (!bulkCarrierDefaultAddress) return;

        setBulkPoint({
            id: `dropoff_default_${bulkCarrier.value}_${bulkCarrierDefaultAddress.locationId || 'na'}`,
            name: 'Direccion por defecto',
            lat: Number(bulkCarrierDefaultAddress.lat),
            lng: Number(bulkCarrierDefaultAddress.lng),
            carrier: bulkCarrier.value,
            address_hint: bulkCarrierDefaultAddress.text,
        });
        setBulkStep(2);
    };

    const submitBulkDropoff = () => {
        setConfirmBulkDropoff(false);
        setBulkSubmitting(true);

        const payload = bulkAction === 'remove'
            ? { order_ids: bulkOrderIds, dropoff: false }
            : {
                order_ids: bulkOrderIds,
                dropoff: true,
                dropoff_carrier: bulkCarrier?.value,
                dropoff_point: bulkPoint,
            };

        tripServices.setOrdersDropoff(tripId, payload)
            .then((data) => {
                setBulkSubmitting(false);
                enqueueSnackbar(
                    bulkAction === 'remove'
                        ? `Dropoff quitado de ${bulkOrderIds.length} pedido(s)`
                        : `Dropoff asignado a ${bulkOrderIds.length} pedido(s)`,
                    { variant: 'success' }
                );
                resetFormState();
                onAdd(data);
            })
            .catch((err) => {
                setBulkSubmitting(false);
                const message = err?.response?.data?.error;
                enqueueSnackbar(
                    _.isArray(message) ? message.join('. ') : (message || 'No se pudo actualizar el dropoff'),
                    { variant: 'error' }
                );
            });
    };

    useEffect(() => {
        if (placeName.length <= 2) {
            setPlacesOpts([]);
            return;
        }

        const timeoutId = setTimeout(() => {
            setPlacesOptsLoading(true)
            var params = encodeParams(_.join([
                "include=location",
                `q[name_cont]=${placeName}`
            ], '&'))
            placeServices.getPlaces(`?${params}`)
                .then(data => {
                    setPlacesOpts(data.data)
                })
                .finally((err) => {
                    setPlacesOptsLoading(false)
                });
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [placeName])

    useEffect(() => {
        if (addMethod !== 'order') return;

        if(!newOrder) {
            setDropoffEnabled(false);
            setDropoffCarrier(null);
            setSelectedDropoffPoint(null);
            return;
        }

        const preloadedPoint = normalizeDropoffPoint(newOrder?.dropoff_point, newOrder?.dropoff_carrier);
        const preloadedCarrier = dropoffCarrierOptions.find((opt) => opt.value === newOrder?.dropoff_carrier) || null;

        if (newOrder?.dropoff && preloadedPoint) {
            setDropoffEnabled(true);
            setDropoffCarrier(preloadedCarrier);
            setSelectedDropoffPoint(preloadedPoint);
            return;
        }

        setDropoffEnabled(false);
        setDropoffCarrier(null);
        setSelectedDropoffPoint(null);
    }, [addMethod, newOrder, dropoffCarrierOptions]);

    useEffect(() => {
        if (addMethod === 'order') return;

        setDropoffEnabled(false);
        setDropoffCarrier(null);
        setSelectedDropoffPoint(null);
    }, [addMethod]);

    useEffect(() => {
        if (addMethod !== 'customer' || !dropoffEnabled) return;
        setSelectedDropoffPoint(null);
    }, [addMethod, dropoffEnabled, newCustomer]);

    useEffect(() => {
        if (addMethod !== 'address' || !dropoffEnabled) return;
        setSelectedDropoffPoint(null);
    }, [addMethod, dropoffEnabled, newPlace, placeAddress]);

    const handleDropoffToggle = (_event, checked) => {
        setDropoffEnabled(checked);

        if (!checked) {
            setDropoffCarrier(null);
            setSelectedDropoffPoint(null);
            return;
        }
    };

    const hasValidDropoffSelection = !dropoffEnabled || Boolean(dropoffCarrier && selectedDropoffPoint);
    const canAddOrder = Boolean(newOrder) && hasValidDropoffSelection;
    const canAddCustomer = Boolean(newCustomer) && hasValidDropoffSelection;
    const canAddPlace = Boolean(newPlace?.id || (placeName && placeAddress)) && hasValidDropoffSelection;

    const dropoffMapContext = useMemo(() => {
        // El paso a paso masivo comparte el mapa del instructivo: publica su
        // contexto para que el padre cargue los puntos del carrier y devuelva el
        // que se clickee, igual que el dropoff de una parada nueva.
        if (addMethod === 'bulk_dropoff') {
            if (bulkAction !== 'assign' || !bulkCarrier || bulkStep < 1) return null;

            return {
                contextId: 'bulk-dropoff',
                carrier: bulkCarrier.value,
                initialQuery: bulkPoint?.address_hint || '',
                selectedPoint: bulkPoint,
            };
        }

        if (!dropoffEnabled || !dropoffCarrier) return null;

        switch (addMethod) {
        case 'order':
            if (!newOrder) return null;
            return {
                contextId: `order-${newOrder.id}`,
                carrier: dropoffCarrier.value,
                initialQuery: selectedDropoffPoint?.address_hint || '',
                selectedPoint: selectedDropoffPoint,
            };
        case 'customer':
            if (!newCustomer) return null;
            return {
                contextId: `customer-${newCustomer.id}`,
                carrier: dropoffCarrier.value,
                initialQuery: selectedDropoffPoint?.address_hint || '',
                selectedPoint: selectedDropoffPoint,
            };
        case 'address': {
            const initialAddress = getPlaceDropoffSearchAddress(newPlace, placeAddress);
            const normalizedAddress = String(initialAddress || '').trim();
            if (!normalizedAddress) return null;

            const contextId = newPlace?.id
                ? `place-${newPlace.id}`
                : `manual-place-${normalizedAddress.toLowerCase()}`;

            return {
                contextId,
                carrier: dropoffCarrier.value,
                initialQuery: selectedDropoffPoint?.address_hint || '',
                selectedPoint: selectedDropoffPoint,
            };
        }
        default:
            return null;
        }
    }, [addMethod, bulkAction, bulkCarrier, bulkPoint, bulkStep, dropoffCarrier, dropoffEnabled, newCustomer, newOrder, newPlace, placeAddress, selectedDropoffPoint]);

    useEffect(() => {
        if (!formNew) return;

        onDropoffMapContextChange(dropoffMapContext);
    }, [dropoffMapContext, formNew, onDropoffMapContextChange]);

    useEffect(() => {
        if (!formNew || !dropoffMapPoint || !dropoffEnabled) return;

        // Al cambiar de carrier, el punto que sigue en el mapa es del ANTERIOR. Sin esta
        // guarda volvia a entrar re-etiquetado con el carrier nuevo —normalizeDropoffPoint
        // le pone el que se le pasa— y el cartel seguia mostrando un punto que ya no
        // corresponde. Peor todavia: si se guardaba asi, la entrega quedaba con el punto de
        // un courier y el `dropoff_carrier` de otro.
        //
        // El padre limpia su punto en el mismo cambio, pero su re-render llega despues de
        // este efecto, asi que acá todavia se ve el viejo.
        const pointCarrier = dropoffMapPoint.carrier;
        if (pointCarrier && dropoffCarrier?.value && pointCarrier !== dropoffCarrier.value) {
            setSelectedDropoffPoint(null);
            return;
        }

        const normalized = normalizeDropoffPoint(dropoffMapPoint, dropoffCarrier?.value);
        // normalizeDropoffPoint devuelve un objeto nuevo cada vez; si el punto no
        // cambió (mismo id) mantenemos la referencia previa para no re-renderizar
        // y evitar el loop de actualización con el contexto del padre.
        setSelectedDropoffPoint((prev) => (prev?.id === normalized?.id ? prev : normalized));
    }, [dropoffCarrier, dropoffEnabled, dropoffMapPoint, formNew]);

    // El punto que se clickea en el mapa entra por la misma via en el paso a paso
    // masivo, pero a su propio estado: aca no hay una parada nueva que crear.
    useEffect(() => {
        if (!formNew || !dropoffMapPoint || addMethod !== 'bulk_dropoff' || bulkAction !== 'assign') return;

        const pointCarrier = dropoffMapPoint.carrier;
        if (pointCarrier && bulkCarrier?.value && pointCarrier !== bulkCarrier.value) {
            setBulkPoint(null);
            return;
        }

        const normalized = normalizeDropoffPoint(dropoffMapPoint, bulkCarrier?.value);
        setBulkPoint((prev) => (prev?.id === normalized?.id ? prev : normalized));
    }, [addMethod, bulkAction, bulkCarrier, dropoffMapPoint, formNew]);

    /**
     * Por que no se puede saltar a un paso, o `null` si se puede.
     *
     * Los botones "Siguiente" ya validaban, pero el titulo de cada paso era clickeable
     * igual, asi que se podia caer en el 3 sin carrier —y el buscador del mapa terminaba
     * pidiendo puntos de un carrier que no existe— o en el 2 sin pedidos elegidos, que no
     * tiene a quien aplicarle nada.
     */
    const bulkStepBlockedReason = (step) => {
        if (step >= 1 && bulkOrderIds.length === 0) return 'Primero completa el paso 1: elige los pedidos';
        if (step >= 2 && !bulkCarrier) return 'Primero completa el paso 2: elige el carrier';
        return null;
    };

    // Funcion y no componente: definir un componente acá lo recrearia en cada render.
    const renderBulkStepLabel = (step, label) => {
        const blocked = bulkStepBlockedReason(step);

        return (
            <Tooltip arrow title={blocked || ''}>
                <StepLabel
                    sx={{cursor: blocked ? 'not-allowed' : 'pointer', py: 0}}
                    onClick={() => { if (!blocked) setBulkStep(step); }}>
                    {label}
                </StepLabel>
            </Tooltip>
        );
    };

    const renderDropoffSection = ({ toggleDisabled = false, searchContextKey = 'dropoff', initialQuery = '' }) => (
        <>
            <Divider />
            <FormControlLabel
                sx={{ m: 0 }}
                label="Asignar dropoff (opcional)"
                disabled={toggleDisabled}
                control={
                    <Switch
                        size="small"
                        checked={dropoffEnabled}
                        onChange={handleDropoffToggle}
                    />
                }
            />
            {dropoffEnabled && (
                <Stack spacing={1}>
                    <Autocomplete
                        size="small"
                        options={dropoffCarrierOptions}
                        value={dropoffCarrier}
                        onChange={(_event, value) => {
                            setDropoffCarrier(value);
                            setSelectedDropoffPoint(null);
                        }}
                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                        getOptionLabel={(option) => option?.label || ''}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Carrier"
                                placeholder="Selecciona un carrier"
                            />
                        )}
                    />
                    {dropoffCarrier && (
                        <CarrierPickupSearch
                            key={`${searchContextKey}-${dropoffCarrier.value}`}
                            carrier={dropoffCarrier.value}
                            initialQuery={selectedDropoffPoint?.address_hint || initialQuery}
                            onSearch={onDropoffSearch}
                            onClear={onDropoffSearchClear}
                            loading={dropoffSearchLoading}
                        />
                    )}
                    <Alert severity={selectedDropoffPoint ? "success" : "warning"} sx={{py: 0}}>
                        {selectedDropoffPoint
                            ? `Punto: ${_.compact([selectedDropoffPoint.name, selectedDropoffPoint.address_hint]).join(' - ')}`
                            : dropoffCarrier
                                ? 'Busca la dirección y elige el punto en el mapa.'
                                : 'Elige un carrier para empezar.'}
                    </Alert>
                </Stack>
            )}
        </>
    );
    

    const renderForm = () => {
        if (authUser?.current_company?.id === 80 || !canManageTrip) {
            return null;
        }
        switch (addMethod) {
        case null:
            return (
                <Stack spacing={1}>
                    <Typography variant={"body1"}>Agregar parada en</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <ActionButtons opts={[
                            {label: "Pedido", tooltip: "Agrega una parada desde un pedido pendiente", variant: "outlined", endIcon: <AddOutlinedIcon />, onClick: () => setAddMethod('order')},
                            {label: "Cliente", tooltip: "Agrega una parada en la dirección de un cliente", variant: "outlined", endIcon: <AddOutlinedIcon />, onClick: () => setAddMethod('customer')},
                            {label: "Otro lugar", tooltip: "Agrega una parada en cualquier dirección, sin cliente", variant: "outlined", endIcon: <AddOutlinedIcon />, onClick: () => setAddMethod('address')}
                        ]}/>
                        {/* Mismo boton que la tarjeta de ruta en planificacion, pero aca
                            trabaja sobre los pedidos que ya estan en el viaje. */}
                        <Tooltip arrow title="Agrega o elimina dropoff">
                            <IconButton size="small" onClick={() => setAddMethod('bulk_dropoff')}>
                                <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            )
        case 'bulk_dropoff': {
            const allBulkSelected = bulkOrderIds.length > 0 && bulkOrderIds.length === bulkSelectableDeliveries.length;
            const canApplyBulk = bulkOrderIds.length > 0
                && (bulkAction === 'remove' || Boolean(bulkCarrier && bulkPoint));

            return (
                <Stack sx={{width: 400}} spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant={"body1"}>Dropoff en</Typography>
                        <Typography color="primary" variant={"body1"}>varios pedidos</Typography>
                        {bulkOrderIds.length > 0 && (
                            <Chip
                                size="small"
                                color="primary"
                                label={bulkOrderIds.length === 1 ? '1 seleccionado' : `${bulkOrderIds.length} seleccionados`}
                            />
                        )}
                    </Stack>

                    {bulkOrderDeliveries.length === 0 ? (
                        <Alert severity="info">
                            Este viaje no tiene pedidos a los que asignarles un dropoff. Las paradas de cliente y de otro lugar se editan al agregarlas.
                        </Alert>
                    ) : (
                        <Stepper activeStep={bulkStep} orientation="vertical" nonLinear>
                            <Step completed={bulkOrderIds.length > 0}>
                                {renderBulkStepLabel(0, 'Pedidos del viaje')}
                                <StepContent>
                                    <Stack spacing={1}>
                                        <ActionButtons opts={[
                                            {
                                                label: "Asignar dropoff",
                                                variant: bulkAction === 'assign' ? 'contained' : 'outlined',
                                                onClick: () => setBulkAction('assign'),
                                            },
                                            {
                                                label: "Quitar dropoff",
                                                color: "state_in_progress",
                                                variant: bulkAction === 'remove' ? 'contained' : 'outlined',
                                                onClick: () => {
                                                    setBulkAction('remove');
                                                    setBulkCarrier(null);
                                                    setBulkPoint(null);
                                                    setBulkStep(0);
                                                },
                                            },
                                        ]}/>
                                        {bulkSelectableDeliveries.length === 0 ? (
                                            <Alert severity="info" sx={{py: 0}}>
                                                Ningún pedido del viaje tiene dropoff asignado.
                                            </Alert>
                                        ) : (
                                        <FormControlLabel
                                            sx={{m: 0}}
                                            label={allBulkSelected ? 'Quitar la selección' : 'Seleccionar todos'}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={allBulkSelected}
                                                    indeterminate={bulkOrderIds.length > 0 && !allBulkSelected}
                                                    onChange={toggleAllBulkOrders}
                                                />
                                            }
                                        />
                                        )}
                                        <Stack sx={{maxHeight: 220, overflowY: 'auto'}}>
                                            {bulkSelectableDeliveries.map((d) => (
                                                <Stack key={`bulk-${d.id}`} direction="row" alignItems="center" spacing={1}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={_.includes(bulkOrderIds, d.order.id)}
                                                        onChange={() => toggleBulkOrder(d.order.id)}
                                                    />
                                                    {/* El numero de parada, igual que el circulito de la
                                                        lista de entregas: es lo que permite ubicar el pedido
                                                        en el viaje sin salir del paso a paso. */}
                                                    <Avatar sx={{width: 22, height: 22, fontSize: 12, flexShrink: 0}}>
                                                        {stopByDeliveryId[d.id]}
                                                    </Avatar>
                                                    <Stack sx={{minWidth: 0}}>
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <Typography variant="body2">{d.order.code}</Typography>
                                                            {/* El que ya tiene dropoff se ve de una: reasignarlo
                                                                pisa el punto anterior. */}
                                                            {d.order.dropoff && (
                                                                <Chip
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="success"
                                                                    label={d.order.dropoff_carrier || 'con dropoff'}
                                                                />
                                                            )}
                                                        </Stack>
                                                        <Typography variant="body2" sx={{color: 'text.secondary'}} noWrap>
                                                            {d.locatable?.name}
                                                        </Typography>
                                                        {/* La direccion a la que va hoy esa entrega. Sale de
                                                            `effective_location` con respaldo en la del cliente,
                                                            que es lo mismo que hace la fila de la entrega
                                                            (ver `deliveryLocation` mas abajo): el cliente con
                                                            varias direcciones mostraba siempre la principal. */}
                                                        {(() => {
                                                            const loc = d.effective_location || d.locatable?.location;
                                                            const texto = [loc?.short_address, loc?.short_zone]
                                                                .filter(Boolean).join(' | ');

                                                            return texto ? (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{color: 'text.disabled'}}
                                                                    noWrap>
                                                                    {texto}
                                                                </Typography>
                                                            ) : null;
                                                        })()}
                                                    </Stack>
                                                </Stack>
                                            ))}
                                        </Stack>
                                        {bulkAction === 'assign' && (
                                            <ActionButtons opts={[
                                                {
                                                    label: "Siguiente",
                                                    color: "success",
                                                    disabled: bulkOrderIds.length === 0,
                                                    onClick: () => setBulkStep(1),
                                                },
                                            ]}/>
                                        )}
                                    </Stack>
                                </StepContent>
                            </Step>

                            {bulkAction === 'assign' && (
                                <Step completed={Boolean(bulkCarrier)}>
                                    {renderBulkStepLabel(1, 'Carrier')}
                                    <StepContent>
                                        <Stack spacing={1}>
                                            <Autocomplete
                                                size="small"
                                                options={dropoffCarrierOptions}
                                                value={bulkCarrier}
                                                onChange={(_event, value) => {
                                                    setBulkCarrier(value);
                                                    setBulkPoint(null);
                                                }}
                                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                getOptionLabel={(option) => option?.label || ''}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Carrier" placeholder="Selecciona un carrier" />
                                                )}
                                            />
                                            {bulkCarrierDefaultAddress && (
                                                <Alert severity="info">
                                                    Dirección por defecto de {bulkCarrier?.label}: {bulkCarrierDefaultAddress.text}
                                                </Alert>
                                            )}
                                            <ActionButtons opts={_.compact([
                                                {label: "Atrás", variant: "text", onClick: () => setBulkStep(0)},
                                                bulkCarrierDefaultAddress && {
                                                    label: "Usar esa dirección",
                                                    color: "success",
                                                    onClick: () => applyBulkDefaultAddress(),
                                                },
                                                {
                                                    label: bulkCarrierDefaultAddress ? "Elegir en el mapa" : "Siguiente",
                                                    color: "success",
                                                    disabled: !bulkCarrier,
                                                    onClick: () => setBulkStep(2),
                                                },
                                            ])}/>
                                        </Stack>
                                    </StepContent>
                                </Step>
                            )}

                            {bulkAction === 'assign' && (
                                <Step completed={Boolean(bulkPoint)}>
                                    {renderBulkStepLabel(2, 'Punto de dropoff')}
                                    <StepContent>
                                        <Stack spacing={1}>
                                            {bulkCarrier ? (
                                                <CarrierPickupSearch
                                                    key={`bulk-dropoff-${bulkCarrier.value}`}
                                                    carrier={bulkCarrier.value}
                                                    initialQuery={bulkPoint?.address_hint || ''}
                                                    onSearch={onDropoffSearch}
                                                    onClear={onDropoffSearchClear}
                                                    loading={dropoffSearchLoading}
                                                />
                                            ) : (
                                                <Alert severity="warning">Primero elige un carrier.</Alert>
                                            )}
                                                                    <Alert severity={bulkPoint ? "success" : "warning"} sx={{py: 0}}>
                                                {bulkPoint
                                                    ? `Punto: ${_.compact([bulkPoint.name, bulkPoint.address_hint]).join(' - ')}`
                                                    : 'Busca la dirección y elige el punto en el mapa.'}
                                            </Alert>
                                            <ActionButtons opts={[
                                                {label: "Atrás", variant: "text", onClick: () => setBulkStep(1)},
                                            ]}/>
                                        </Stack>
                                    </StepContent>
                                </Step>
                            )}
                        </Stepper>
                    )}

                    <ActionButtons opts={[
                        {
                            label: "Cerrar",
                            color: "state_in_progress",
                            disabled: bulkSubmitting,
                            endIcon: <DeleteOutlineOutlinedIcon />,
                            onClick: () => resetFormState(),
                        },
                        {
                            // "Quitar" se queda en la accion de quitar: ahi el boton borra el
                            // dropoff de los pedidos marcados, y decir "Agregar" con el icono de
                            // sumar diria exactamente lo contrario de lo que hace.
                            label: bulkAction === 'remove' ? "Quitar" : "Agregar",
                            color: "success",
                            loading: bulkSubmitting,
                            disabled: bulkSubmitting || !canApplyBulk,
                            endIcon: bulkAction === 'remove'
                                ? <LocalShippingOutlinedIcon />
                                : <AddCircleOutlineOutlinedIcon />,
                            onClick: () => setConfirmBulkDropoff(true),
                        },
                    ]}/>
                </Stack>
            )
        }
        case 'order':
            return (
                <Stack sx={{width: 400}} spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant={"body1"}>Agregar</Typography>
                        <Typography color="primary" variant={"body1"}>pedido</Typography>
                        {/* Contador de lo que se lleva marcado, al lado del titulo. */}
                        {selectedOrders.length > 0 && (
                            <Chip
                                size="small"
                                color="primary"
                                label={selectedOrders.length === 1
                                    ? '1 seleccionado'
                                    : `${selectedOrders.length} seleccionados`}
                            />
                        )}
                    </Stack>
                    <OrdersPicker
                        tripId={tripId}
                        selectedIds={selectedOrderIds}
                        onToggle={toggleOrder}
                        disabled={addingStop}
                    />
                    {/* El comentario es uno solo y se copia en todas las entregas que se crean. */}
                    <TextField
                        fullWidth
                        size="small"
                        label="Comentario"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                    <FormControlLabel label="Asignar todos los productos" sx={{m: 0}} disabled control={<Switch defaultChecked size="small" />} />
                    <ActionButtons opts={[
                        {label: "Cerrar", color: "state_in_progress", disabled: addingStop, endIcon: <DeleteOutlineOutlinedIcon />, onClick: () => resetFormState()},
                        {label: "Agregar", color: "success", loading: addingStop, disabled: addingStop || selectedOrders.length === 0, endIcon: <AddCircleOutlineOutlinedIcon />, onClick: () => setConfirmAddOrders(true)}
                    ]}/>
                </Stack>
            )
        case 'customer':
            return (
                <Stack sx={{width: 400}} spacing={1}>
                    <Stack direction="row" spacing={0.5}>
                        <Typography variant={"body1"}>Agregar parada en</Typography>
                        <Typography color="primary" variant={"body1"}>cliente</Typography>
                    </Stack>
                    <Autocomplete
                        id="new-customer-select"
                        size="small"
                        freeSolo
                        fullWidth
                        openOnFocus
                        autoComplete
                        noOptionsText="No hay resultados"
                        onInputChange={_.debounce((e, v) => { if(v) { getCustomersOptions(v) } }, 500)}
                        options={customersOpts}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        renderOption={(props, option) => {
                            return (
                                <li {...props}>
                                    <Stack spacing={0.5}>
                                        <Stack direction="row" spacing={0.5}>
                                            <Typography variant="body1">{option.code}</Typography>
                                            <Typography variant="body2">{option.name}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={0.5}>
                                            <Typography variant="body2" sx={{ color: "text.secondary" }}>{option.location?.short_address} | {option.location?.short_zone}</Typography>
                                        </Stack>
                                    </Stack>
                                </li>
                            )
                        }}
                        value={newCustomer}
                        onChange={(e, v) => { setNewCustomer(v || null) }}
                        renderInput={(params) => 
                            <TextField {...params}
                                label="Cliente"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <React.Fragment>
                                            {customersOptsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </React.Fragment>
                                    ),
                                }} />
                        }
                    />
                    <TextField
                        fullWidth
                        size="small"
                        label="Comentario"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                    {renderDropoffSection({
                        toggleDisabled: !newCustomer,
                        searchContextKey: dropoffMapContext?.contextId || `customer-${newCustomer?.id || 'new'}`,
                    })}
                    <ActionButtons opts={[
                        {label: "Cerrar", color: "state_in_progress", disabled: addingStop, endIcon: <DeleteOutlineOutlinedIcon />, onClick: () => setAddMethod(null)},
                        {label: "Agregar", color: "success", loading: addingStop, disabled: addingStop || !canAddCustomer, endIcon: <AddCircleOutlineOutlinedIcon />, onClick: () => addByCustomer()}
                    ]}/>
                </Stack>
            )
        case 'address':
            return (
                <Stack sx={{width: 400}} spacing={1}>
                    <Stack direction="row" spacing={0.5}>
                        <Typography variant={"body1"}>Agregar parada en</Typography>
                        <Typography color="primary" variant={"body1"}>otro lugar</Typography>
                    </Stack>
                    { newPlace ?
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Stack>
                                <Typography variant="body2">{newPlace.name}</Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>{newPlace.location?.short_address}</Typography>
                            </Stack>
                            <ActionButtons opts={[
                                {label: "Cambiar", color: "primary", endIcon: <ChangeCircleOutlinedIcon />, onClick: () => setNewPlace(null)}
                            ]}/>
                        </Stack>
                    :
                        <Stack direction="row" alignItems="center">
                            <Autocomplete
                                id="new-place-select"
                                size="small"
                                freeSolo
                                openOnFocus
                                autoComplete
                                noOptionsText="No hay resultados"
                                onInputChange={(e,v) => { setPlaceName(v) }}
                                options={placesOpts}
                                getOptionLabel={(option) => `${option.name}`}
                                renderOption={(props, option) => {
                                    return (
                                        <li {...props}>
                                            <Stack spacing={0.5}>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Typography variant="body1">{option.code}</Typography>
                                                    <Typography variant="body2">{option.name}</Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>{option.location?.short_address} | {option.location?.short_zone}</Typography>
                                                </Stack>
                                            </Stack>
                                        </li>
                                    )
                                }}
                                value={newPlace}
                                onChange={(e, v) => { setNewPlace(v || null) }}
                                renderInput={(params) => 
                                    <TextField {...params}
                                        label="Lugar"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {placesOptsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                        sx={{ width: 200 }}/>
                                }
                            />
                            <MapsAutocomplete
                                onPlaceChanged={onPlaceChanged}
                                onLoad={onLoad}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Ubicación"
                                        disabled={newPlace?.id}
                                        sx={{ width: 200 }}/>
                            </MapsAutocomplete>
                        </Stack>
                    }
                    <TextField
                        fullWidth
                        size="small"
                        label="Comentario"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                    {renderDropoffSection({
                        toggleDisabled: !(newPlace?.id || String(placeAddress || '').trim().length > 0),
                        searchContextKey: dropoffMapContext?.contextId || `place-${newPlace?.id || 'manual'}`,
                    })}
                    <ActionButtons opts={[
                        {label: "Cerrar", color: "state_in_progress", disabled: addingStop, endIcon: <DeleteOutlineOutlinedIcon />, onClick: () => setAddMethod(null)},
                        {label: "Agregar", color: "success", loading: addingStop, disabled: addingStop || !canAddPlace, endIcon: <AddCircleOutlineOutlinedIcon />, onClick: () => addByPlace()}
                    ]}/>
                </Stack>
            )
        default:
            return null;
        }
    }

    if(formNew) {
        return (
            <React.Fragment>
            <ListItem
                id='newDelivery'
                key='newDelivery'
                alignItems="flex-start">

                <ListItemAvatar sx={{ mt: 0 }}></ListItemAvatar>

                <ListItemText id={`lit_newDelivery`}
                    primary={renderForm()}
                />
            </ListItem>
            <Dialog
                open={addressChoice.open}
                onClose={() => setAddressChoice({ open: false, addresses: [], label: '', onSelect: null })}
                fullWidth
                maxWidth="sm">
                <DialogTitle>
                    <Typography variant="h3" color="primary">Elegir dirección de entrega</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        {addressChoice.label} tiene más de una dirección. ¿Cuál quieres usar en esta ruta?
                    </Typography>
                    <Stack spacing={1}>
                        {(addressChoice.addresses || []).map((addr, idx) => (
                            <Button
                                key={`ac-${addr.id}`}
                                variant="outlined"
                                fullWidth
                                onClick={() => addressChoice.onSelect && addressChoice.onSelect(addr)}
                                endIcon={addr.primary ? <PrimaryAddressChip /> : null}
                                // textAlign porque un <button> centra su texto: la dirección que
                                // no cabía en una línea quedaba con la segunda línea centrada.
                                sx={{ justifyContent: 'space-between', textTransform: 'none', textAlign: 'left' }}>
                                {/* Solo la dirección: el "Dirección N" le robaba el ancho y la partía en dos
                                    líneas. El número queda de respaldo para las que no tienen calle. */}
                                <span>{[addr.short_address?.trim(), addr.short_zone].filter(Boolean).join(' · ') || `Dirección ${idx + 1}`}</span>
                            </Button>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddressChoice({ open: false, addresses: [], label: '', onSelect: null })}>Cerrar</Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={confirmAddOrders}
                title="Agregar pedidos al viaje"
                message={
                    <>
                        ¿Estás seguro de que quieres agregar {selectedOrders.length === 1
                            ? <>el pedido <strong>{selectedOrders[0]?.code}</strong></>
                            : <>los <strong>{selectedOrders.length} pedidos</strong> seleccionados</>} al viaje <strong>{tripId}</strong>?
                        <br/>
                        {selectedOrders.length === 1
                            ? 'Se crea una entrega con todos sus productos.'
                            : 'Se crea una entrega por pedido, con todos sus productos.'}
                    </>
                }
                confirmText="Confirmar"
                confirmColor="success"
                loading={addingStop}
                onClose={() => setConfirmAddOrders(false)}
                onConfirm={() => submitAddByOrders()}
            />
            <ConfirmDialog
                open={Boolean(confirmReassignOrders)}
                title={confirmReassignOrders?.length === 1 ? "Pedido en otro viaje" : "Pedidos en otro viaje"}
                message={reassignOrdersMessage(confirmReassignOrders || [])}
                confirmText="Traer los pedidos"
                confirmColor="success"
                loading={addingStop}
                onClose={() => setConfirmReassignOrders(null)}
                onConfirm={() => {
                    setConfirmReassignOrders(null);
                    submitAddByOrders(true);
                }}
            />
            <ConfirmDialog
                open={confirmBulkDropoff}
                title={bulkAction === 'remove' ? "Quitar dropoff" : "Asignar dropoff"}
                message={
                    bulkAction === 'remove'
                        ? <>Se quitará el dropoff de <strong>{bulkOrderIds.length} pedido(s)</strong> del viaje <strong>{tripId}</strong>. Las entregas vuelven a la dirección del cliente.</>
                        : <>Se asignará el dropoff de <strong>{bulkCarrier?.label}</strong> en <strong>{_.compact([bulkPoint?.name, bulkPoint?.address_hint]).join(' - ')}</strong> a <strong>{bulkOrderIds.length} pedido(s)</strong> del viaje <strong>{tripId}</strong>. A los que ya tenían un dropoff se les reemplaza.</>
                }
                confirmText="Confirmar"
                confirmColor="success"
                loading={bulkSubmitting}
                onClose={() => setConfirmBulkDropoff(false)}
                onConfirm={() => submitBulkDropoff()}
            />
            <ConfirmDialog
                open={Boolean(confirmReassign)}
                title="Pedido en otro viaje"
                message={reassignMessage(confirmReassign)}
                confirmText="Traer el pedido"
                onClose={() => setConfirmReassign(null)}
                onConfirm={() => {
                    setConfirmReassign(null);
                    submitAddByOrder(true).catch(() => setAddingStop(false));
                }}
            />
            </React.Fragment>
        )
    } else {
        const {code, state, state_name, locatable, planned_end, effective_location} = delivery;
        // Con respaldo en el pedido: el dropoff se define sobre el pedido y la entrega lo
        // hereda, pero segun el serializer con el que venga la entrega puede llegar sin el.
        const dropoffEnabled = Boolean(delivery.dropoff ?? delivery.order?.dropoff);
        const dropoffCarrierKey = delivery.dropoff_carrier || delivery.order?.dropoff_carrier;
        const dropoffPointName = delivery.dropoff_name || delivery.order?.dropoff_name;
        // La direccion elegida al planificar cuando el cliente tiene varias.
        // locatable.location siempre trae la principal, asi que la fila mostraba
        // una direccion distinta de la que rutea el mapa.
        const deliveryLocation = effective_location || locatable?.location;
        const isPlanningTrip = _.includes(['planned', 'pending'], tripState);
        const canRemoveDelivery = (isPlanningTrip && !_.includes(['cancelled', 'finished'], delivery.state)) ||
            (tripState === 'in_progress' && !_.includes(['finished'], delivery.state));
        // Icono de mapa, igual que en Clientes: verde si el cliente tiene direccion, rojo si
        // no. Abre sus direcciones y geocercas para agregar otra y elegir cual usa ESTA
        // parada. Con dropoff la parada va al punto del courier, asi que ahi solo se editan
        // las direcciones del cliente, sin cambiar la de la parada.
        const isCustomerStop = locatable?.type === 'Customer';
        const customerAddressCount = locatable?.addresses?.length || 0;
        const customerHasAddress = customerAddressCount > 0 || Boolean(locatable?.location?.path);
        // Hora real de una entrega finalizada: la de la recepcion, que es cuando el cliente
        // recibio. `final_end` va de respaldo pero es mas tarde: se marca recien cuando el
        // chofer pasa a la parada siguiente.
        const realEnd = state === 'finished' ? (delivery.receipt_completed_at || delivery.final_end) : null;
        const timeFormat = 'DD MMM YYYY [a las] HH:mm';
        const timeTooltip = (() => {
            const planned = planned_end ? `Planificada: ${moment(planned_end).format(timeFormat)}` : 'Sin hora planificada';
            if (!realEnd) return planned_end ? `Planificada para el ${moment(planned_end).format(timeFormat)}` : planned;
            const lines = [planned, `Entregada: ${moment(realEnd).format(timeFormat)}`];
            if (planned_end) {
                const diff = moment(realEnd).diff(moment(planned_end), 'minutes');
                const abs = Math.abs(diff);
                const amount = abs >= 60 ? `${Math.floor(abs / 60)} h ${abs % 60} min` : `${abs} min`;
                lines.push(abs < 1 ? 'A la hora planificada' : `${amount} ${diff > 0 ? 'tarde' : 'antes'}`);
            }
            return <span style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</span>;
        })();
        const canChangeStopAddress = canManageTrip && !dropoffEnabled && !_.includes(['cancelled', 'finished'], delivery.state);
        const changeStopAddress = (addr) => deliveryServices.setPlannedAddress(delivery.id, addr)
            .then(() => {
                enqueueSnackbar('Dirección de la parada actualizada. Haz click en Optimizar para volver a asignar la ruta.', { variant: 'warning' });
                setOpenLocDialog(false);
                // La parada cambio de lugar: el instructivo pide volver a optimizar.
                onAddressChanged(delivery, { stopMoved: true });
            })
            .catch((err) => {
                enqueueSnackbar(err?.response?.data?.error || 'No se pudo cambiar la dirección de la parada', { variant: 'error' });
            });
        return (
            <React.Fragment>
            <ListItem
                id={delivery.id}
                key={delivery.id}
                // alignItems="flex-start"
                // Con el modo seleccion prendido el click marca la entrega en vez de
                // abrirla: es la unica forma de poder marcar tocando la fila completa
                // y no solo el cuadradito.
                onClick={() => {
                    if (selectMode) {
                        if (canRemoveDelivery) onToggleSelectForRemoval(delivery.id);
                        return;
                    }
                    onSelect(delivery);
                }}
                sx={{
                    zIndex: 100000,
                    cursor: canSort ? 'grab' : 'auto',
                    // MUI reserva 48px fijos a la derecha para el `secondaryAction`, que no
                    // alcanzan para la columna de hora y estado mas el tacho. Sin esto el
                    // texto de la entrega se le mete por debajo.
                    pr: (canManageTrip && canRemoveDelivery && !selectMode ? 27 : 23) + (isCustomerStop && !selectMode ? 6 : 0) + (realEnd ? 10 : 2),
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                        backgroundColor: "#f5f5f5"
                    },
                }}
                secondaryAction={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        {isCustomerStop && !selectMode &&
                            <Tooltip title={customerHasAddress ? "Editar dirección y geocerca" : "El cliente no tiene dirección: agregar dirección y geocerca"}>
                                <IconButton
                                    color={customerHasAddress ? 'success' : 'error'}
                                    // La fila abre la entrega al hacer click: sin esto se abrian las dos cosas.
                                    onClick={(e) => { e.stopPropagation(); setOpenLocDialog(true); }}>
                                    <Badge badgeContent={customerAddressCount} color="primary" showZero max={9} sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>
                                        <PlaceOutlinedIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        }
                        <Tooltip arrow title={timeTooltip}>
                            <Chip
                                size="small"
                                label={`P: ${planned_end ? moment(planned_end).format('HH:mm') : '-'}${realEnd ? `  R: ${moment(realEnd).format('HH:mm')}` : ''}`}
                                icon={<AccessTimeOutlinedIcon fontSize="small"/>}
                                sx={{width: realEnd ? 150 : 90}}
                            />
                        </Tooltip>
                        <StateBadge state={state} stateName={state_name} sx={{width: 100}}/>
                        {/* En modo seleccion la papelera por fila se esconde: el borrado
                            lo hace la barra de seleccionadas y tener las dos cosas a la
                            vez invita a borrar de a una creyendo que borra las marcadas. */}
                        {canManageTrip && canRemoveDelivery && !selectMode &&
                            <TrashIconButton
                                size="medium"
                                title="Eliminar entrega del viaje"
                                disabled={removingStopId === delivery.id}
                                onClick={() => { setConfirmRemoveDelivery(true) }}>
                                {removingStopId === delivery.id ? <CircularProgress size={20} /> : null}
                            </TrashIconButton>
                        }
                    </Stack>
                }>
                
                {selectMode && (
                    /* El <span> es necesario para el caso deshabilitado: un Tooltip sobre
                       un control deshabilitado no recibe eventos y no llega a mostrarse,
                       que es justo cuando hay algo que explicar. */
                    <Tooltip
                        arrow
                        title={canRemoveDelivery
                            ? ''
                            : 'Una entrega finalizada o cancelada no se puede eliminar del viaje'}>
                        <span>
                            <Checkbox
                                edge="start"
                                size="small"
                                disableRipple
                                checked={selectedForRemoval}
                                disabled={!canRemoveDelivery}
                                // El click ya lo maneja la fila; sin esto el toggle se
                                // dispara dos veces y la entrega queda como estaba.
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => onToggleSelectForRemoval(delivery.id)}
                                sx={{mr: 0.5}}
                            />
                        </span>
                    </Tooltip>
                )}

                <ListItemAvatar>
                    <Avatar>
                        {stop}
                    </Avatar>
                </ListItemAvatar>
    
                <ListItemText id={`lit_${delivery.id}`}
                    primary={
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}>
                            {/* Era 50% para no meterse debajo del bloque de la derecha, que
                                antes se extendia el doble de ancho. */}
                            <Stack sx={{width: '100%'}}>
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <Typography variant={"body1"} mb={0}>{tripId} - {code} {delivery?.order?.code && `(P: ${delivery?.order?.code})`}</Typography>
                                    {dropoffEnabled && dropoffCarrierKey && (
                                        <DropoffCarrierMark
                                            carrierKey={dropoffCarrierKey}
                                            pointName={dropoffPointName}
                                            carriers={companySettings?.available_dropoff_carriers}
                                        />
                                    )}
                                </Stack>
                                <Typography variant={"body1"} mb={0}>{locatable?.code} {locatable?.name}</Typography>
                            </Stack>
                        </Stack>
                    }
                    secondary={
                        <React.Fragment>
                            {/* Con dropoff la parada es el punto del courier, no la casa del
                                comprador: mostrar la del comprador (Valdivia, Hualpen...) hacia
                                que la fila no calzara con el pin del mapa, que si va al punto. */}
                            {dropoffEnabled ? (
                                <React.Fragment>
                                    <Typography>
                                        Dropoff {getDropoffCarrierByKey(dropoffCarrierKey, companySettings?.available_dropoff_carriers)?.label || dropoffCarrierKey}
                                        {dropoffPointName ? ` · ${dropoffPointName}` : ''}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                                        Destino final: {deliveryLocation?.short_address} | {deliveryLocation?.short_zone}
                                    </Typography>
                                </React.Fragment>
                            ) : (
                                <Typography>{deliveryLocation?.short_address} | {deliveryLocation?.short_zone}</Typography>
                            )}
                            { delivery.comment &&
                                <Tooltip title="Comentario de despacho">
                                    <Alert severity="warning" icon={<CommentOutlinedIcon fontSize="small" />} sx={{ '&.MuiAlert-root': { display: 'inline-flex', px: 0.5, py: 0, fontSize: 11 } }}>
                                        {delivery.comment}
                                    </Alert>
                                </Tooltip>
                            }
                        </React.Fragment>
                    }
    
                />
            </ListItem>
            <ConfirmDialog
                open={confirmRemoveDelivery}
                title={isLastDelivery ? "Eliminar la entrega y el viaje" : "Eliminar entrega del viaje"}
                message={
                    <>
                        ¿Estás seguro que deseas eliminar {delivery?.order?.code ? <>el pedido <strong>{delivery.order.code}</strong></> : <>la entrega <strong>{code}</strong></>} del viaje <strong>{tripId}</strong>?
                        <br />
                        {/* Un viaje sin entregas no se puede cerrar ni completar, asi que el
                            backend se lo lleva junto con la ultima entrega. Se avisa acá
                            porque es lo unico irreversible de la operacion. */}
                        { isLastDelivery
                            ? <>Es su <strong>única entrega</strong>, así que también se elimina el viaje <strong>{tripId}</strong> completo. Esto no se puede deshacer. El pedido vuelve a quedar disponible para planificar.</>
                            : 'El pedido vuelve a quedar disponible para planificar.' }
                    </>
                }
                loading={removingStopId === delivery.id}
                onClose={() => setConfirmRemoveDelivery(false)}
                onConfirm={() => removeDelivery()}
            />
            {isCustomerStop &&
                <LocationDialog
                    open={openLocDialog}
                    onClose={() => setOpenLocDialog(false)}
                    customer={locatable}
                    isLoaded={mapsLoaded}
                    // Agregar o borrar una direccion cambia el contador y el color del icono.
                    onChanged={() => onAddressChanged(delivery)}
                    stop={canChangeStopAddress ? {
                        locationId: deliveryLocation?.id,
                        // Para mostrar la direccion actual cuando es solo del pedido y no esta
                        // entre las del cliente.
                        location: deliveryLocation,
                        label: `Parada ${stop}${delivery?.order?.code ? ` · Pedido ${delivery.order.code}` : ''}`,
                        onChoose: changeStopAddress,
                    } : null}
                />
            }
            </React.Fragment>
        );
    }
};

export default SortableElement(DeliveryCell);
